import React, { useEffect, useState } from 'react';
import { useCpPortal } from '../../hooks/useCpPortal';

export interface ClientBrandingInfo {
  displayName?: string | null;
  hasLogo?: boolean;
  logoUrl?: string | null;
  primaryColor?: string | null;
  logoMeta?: { width: number; height: number; shape?: string } | null;
}

interface ClientPartnerBrandBarProps {
  branding?: ClientBrandingInfo | null;
  companyName?: string | null;
  className?: string;
  compact?: boolean;
  /** When a logo loads, hide the duplicate legal name (logo is the brand). */
  hideNameWhenLogo?: boolean;
  tone?: 'light' | 'dark';
}

/**
 * Co-branded partner lockup. Loads the logo via the authenticated portal API
 * (blob URL) so admin-mirror and CP-login both work — img src cannot send JWT.
 */
export const ClientPartnerBrandBar: React.FC<ClientPartnerBrandBarProps> = ({
  branding,
  companyName,
  className = '',
  compact = false,
  hideNameWhenLogo = true,
  tone = 'light',
}) => {
  const { apiBase, authHeaders } = useCpPortal();
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);
  const partnerName = branding?.displayName || companyName || null;
  const wantsLogo = !!(branding?.hasLogo || branding?.logoUrl);

  useEffect(() => {
    if (!wantsLogo) {
      setLogoBlobUrl(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/branding/logo`, { headers: authHeaders() });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) return;
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setLogoBlobUrl(objectUrl);
      } catch {
        /* non-blocking — name remains visible */
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [wantsLogo, apiBase, authHeaders]);

  const showLogo = !!logoBlobUrl;
  const showName = !!partnerName && !(hideNameWhenLogo && showLogo);

  if (!showLogo && !showName) return null;

  return (
    <div className={`flex min-w-0 items-center gap-2.5 ${className}`}>
      {showLogo && (
        <img
          src={logoBlobUrl!}
          alt=""
          className={`flex-shrink-0 object-contain object-left ${
            compact ? 'h-8 max-w-[140px]' : 'h-10 max-w-[180px]'
          }`}
        />
      )}
      {showName && (
        <p
          className={`min-w-0 truncate font-semibold ${
            tone === 'dark' ? 'text-white' : 'text-[var(--foreground)]'
          } ${compact ? 'text-sm' : 'text-base'}`}
        >
          {partnerName}
        </p>
      )}
    </div>
  );
};
